import { graphql, useStaticQuery } from 'gatsby';
import kebabCase from 'lodash.kebabcase';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import {
  Drupal_ParagraphFeatureSection,
  Drupal_ParagraphParagraphBundle
} from '../../generated/graphql';
import { isDefined } from '../../types/types';
import { getProcessedValue } from '../../utils/helpers';
import linkParser from '../../utils/link-parser';
import Link from '../link/link';
import './feature-highlights.scss';

enum LinkPlacement {
  UnderHeaders = 'under headers',
  BelowParagraphs = 'below paragraphs'
}

interface Props {
  featureSection: Drupal_ParagraphFeatureSection;
  text?: Drupal_ParagraphParagraphBundle[] | null;
  linkText?: string;
  linkPath?: {
    path?: string | null;
  };
  defaultImages?: boolean;
}

const FeatureHighlights = ({
  featureSection: {
    fieldFeatureHeader: header,
    fieldFeatureSubheader: subtitle,
    fieldFeatureSectionLayout: layout = 'Four Wide',
    fieldFeatureSectionBackground: background = 'White',
    fieldFeatureSectionLinkPlace: linkPlacement,
    fieldImageLink: backgroundImage,
    fieldFeatureImageType: imageSize = 'Big Icon'
  },
  text,
  linkText,
  linkPath,
  defaultImages = false
}: Props) => {
  const backgroundClass = background ? `feature-highlights--${background}` : '';

  const backgroundImageStyle = backgroundImage
    ? { backgroundImage: `url(${backgroundImage})` }
    : {};
  const layoutClass = layout ? `column-${kebabCase(layout)}` : '';
  const imageSizeClass = imageSize ? `image-${kebabCase(imageSize)}` : '';
  const linkPlacementNormalized = linkPlacement
    ? linkPlacement.toLowerCase()
    : '';

  const { allColumnImagesJson } = useStaticQuery(
    graphql`
      {
        allColumnImagesJson {
          edges {
            node {
              src
              alt
            }
          }
        }
      }
    `
  );

  return (
    <section
      className={`feature-highlights ${backgroundClass}`}
      style={backgroundImageStyle}
    >
      <div className="lucid-container padding-t-sm padding-b-sm">
        <h2 className="feature__header">{header}</h2>
        <p className="feature__subheader">
          {subtitle}
          {linkPlacementNormalized === LinkPlacement.UnderHeaders &&
            linkPath &&
            linkPath.path && (
              <>
                {` `}
                <Link
                  secondary
                  hasArrow
                  to={linkPath.path}
                  className="link link--subheader"
                >
                  {linkText}
                </Link>
              </>
            )}
        </p>

        <ul className="feature__column">
          {text &&
            text
              .filter(isDefined)
              // Check if array items have content due to translation quirk
              .filter(item => item.fieldHeader || item.fieldImageLink)
              .map((column, i) => (
                <li
                  className={`feature__column__item ${layoutClass}`}
                  key={column.fieldHeader || `feature-column-item-${i}`}
                >
                  {column.fieldImageLink && (
                    <img
                      className={`feature__column__img ${imageSizeClass}`}
                      src={column.fieldImageLink}
                      alt={column.fieldAlt || ''}
                    />
                  )}

                  {defaultImages && (
                    <img
                      className={`feature__column__img ${imageSizeClass}`}
                      src={allColumnImagesJson.edges[i].node.src}
                      alt="Lucid Features"
                    />
                  )}
                  {column.fieldHeader && (
                    <h3 className="feature__column__header">
                      {column.fieldHeader}
                    </h3>
                  )}
                  <div className="feature__column__text">
                    {ReactHtmlParser(
                      getProcessedValue(column.fieldParagraphText),
                      {
                        transform: linkParser
                      }
                    )}
                  </div>
                </li>
              ))}
        </ul>
        {linkPlacementNormalized === LinkPlacement.BelowParagraphs &&
          linkPath &&
          linkPath.path && (
            <Link
              primary
              asButton
              to={linkPath.path}
              className="feature-highlights__cta"
            >
              {linkText}
            </Link>
          )}
      </div>
    </section>
  );
};

export default FeatureHighlights;
